import React, { useState, useEffect } from 'react';
import { BusinessModel, Template } from '../types/template';
import { TemplateService } from '../services/templateService';
import './TemplateSelectionModal.css';

interface TemplateSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  businessModel: BusinessModel | null;
  categoryId?: string | null;
  transportationMethod?: string | null;
  onSelectTemplate: (template: Template) => void;
  currentUserRole?: string;
  onEditTemplate?: (template: Template) => void;
  onCreateTemplate?: () => void;
}

const TemplateSelectionModal: React.FC<TemplateSelectionModalProps> = ({
  isOpen,
  onClose,
  onBack,
  businessModel,
  categoryId,
  transportationMethod,
  onSelectTemplate,
  currentUserRole,
  onEditTemplate,
  onCreateTemplate
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && businessModel) {
      loadTemplates();
    }
  }, [isOpen, businessModel, categoryId, transportationMethod]);

  const loadTemplates = async () => {
    if (!businessModel) return;

    setLoading(true);
    setError(null);
    try {
      const templateList = await TemplateService.getTemplatesByBusinessModel(businessModel.id);
      
      // Filter by category if specified
      let filteredTemplates = categoryId 
        ? templateList.filter(t => t.categoryId === categoryId)
        : templateList;
      
      // Filter by transportation method if specified
      if (transportationMethod) {
        filteredTemplates = filteredTemplates.filter(t => 
          t.defaultTransportationVehicle === transportationMethod
        );
      }
      
      setTemplates(filteredTemplates);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template: Template) => {
    onSelectTemplate(template);
    onClose();
  };

  const getCategoryIcon = (categoryId: string) => {
    if (!businessModel) return '📋';
    const category = businessModel.categories.find(cat => cat.id === categoryId);
    return category?.icon || '📋';
  };

  const getCategoryName = (categoryId: string) => {
    if (!businessModel) return 'Unknown Category';
    const category = businessModel.categories.find(cat => cat.id === categoryId);
    return category?.name || 'Unknown Category';
  };

  if (!isOpen || !businessModel) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="template-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          {onBack && (
            <button className="back-btn" onClick={onBack} title="Back to Business Models">
              ← Back
            </button>
          )}
          <div className="header-content">
            <div className="header-icon">{businessModel.icon}</div>
            <div>
              <h2>{businessModel.name}</h2>
              <p className="header-description">{businessModel.description}</p>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading">Loading templates...</div>
          ) : error ? (
            <div className="error">
              <p>{error}</p>
              <button onClick={loadTemplates} className="retry-btn">
                🔄 Retry
              </button>
            </div>
          ) : templates.length === 0 ? (
            <div className="no-templates">
              <div className="no-templates-icon">📋</div>
              <h3>No Templates Available</h3>
              <p>There are no templates available for {businessModel.name} yet.</p>
              {currentUserRole === 'MASTER' && onCreateTemplate ? (
                <button onClick={onCreateTemplate} className="create-template-btn-large">
                  ➕ Create First Template
                </button>
              ) : (
                <p className="no-templates-note">
                  Contact your administrator to create templates for this business model.
                </p>
              )}
            </div>
          ) : (
            <>
              {currentUserRole === 'MASTER' && onCreateTemplate && (
                <div className="create-template-header">
                  <button onClick={onCreateTemplate} className="create-template-btn">
                    ➕ Create New Template
                  </button>
                </div>
              )}
            <div className="templates-grid">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className={`template-card ${template.isDefault ? 'default-template' : ''}`}
                  onClick={() => handleTemplateSelect(template)}
                >
                  {template.isDefault && (
                    <div className="default-badge">Default</div>
                  )}
                  
                  {currentUserRole === 'MASTER' && onEditTemplate && (
                    <button
                      className="edit-template-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditTemplate(template);
                      }}
                      title="Edit Template"
                    >
                      ✏️
                    </button>
                  )}
                  
                  <div className="template-icon">
                    {getCategoryIcon(template.categoryId)}
                  </div>
                  
                  <div className="template-content">
                    <h3 className="template-title">{template.name}</h3>
                    <p className="template-description">{template.description}</p>
                    
                    <div className="template-meta">
                      <div className="template-category">
                        <span className="category-icon">{getCategoryIcon(template.categoryId)}</span>
                        <span className="category-name">{getCategoryName(template.categoryId)}</span>
                      </div>
                      
                      <div className="template-stats">
                        <div className="stat">
                          <span className="stat-label">Tasks:</span>
                          <span className="stat-value">{template.tasks.length}</span>
                        </div>
                        <div className="stat">
                          <span className="stat-label">Usage:</span>
                          <span className="stat-value">{template.usageCount || 0}</span>
                        </div>
                      </div>
                    </div>

                    <div className="template-preview">
                      <strong>Transportation:</strong>
                      <span className="transportation-method">
                        {template.defaultTransportationVehicle}
                      </span>
                    </div>

                    {template.tasks.length > 0 && (
                      <div className="template-tasks-preview">
                        <strong>Sample Tasks:</strong>
                        <div className="tasks-list">
                          {template.tasks.slice(0, 3).map((task, index) => (
                            <div key={index} className="task-preview">
                              <span className="task-name">{task.name}</span>
                              <span className="task-status">{task.status}</span>
                            </div>
                          ))}
                          {template.tasks.length > 3 && (
                            <div className="task-preview more">
                              +{template.tasks.length - 3} more tasks
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="template-arrow">→</div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelectionModal;

